import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  applyNodeChanges,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";

import {
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SaveIcon from "@mui/icons-material/Save";
import { makeStyles } from "tss-react/mui";
import { toast } from "../../services/toast";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";
import { i18n } from "../../translate/i18n";

const ROOT_ID = "__root__";
const NODE_WIDTH = 210;
const NODE_HEIGHT = 90;

const useStyles = makeStyles()((theme) => ({
  wrapper: {
    position: "relative",
    width: "100%",
    height: "100%",
    minHeight: 560,
    flex: 1,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    overflow: "hidden",
    background: theme.palette.mode === "light" ? "#f8f9fb" : theme.palette.background.default,
  },
  toolbar: {
    position: "absolute",
    top: 8,
    left: 8,
    zIndex: 5,
    display: "flex",
    gap: theme.spacing(1),
  },
  editorPanel: {
    position: "absolute",
    top: 0,
    right: 0,
    width: 320,
    height: "100%",
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderLeft: `1px solid ${theme.palette.divider}`,
    boxShadow: "-2px 0 8px rgba(0,0,0,0.08)",
    zIndex: 6,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    gap: theme.spacing(1.5),
    overflowY: "auto",
  },
  editorHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  editorActions: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: "auto",
  },
  mediaRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: 13,
  },
  fieldRow: {
    display: "flex",
    gap: theme.spacing(1),
  },
  numberField: {
    width: 88,
    flexShrink: 0,
  },
  mediaDropzone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    padding: theme.spacing(2),
    border: `1.5px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    color: theme.palette.text.secondary,
    fontSize: 13,
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  mediaDropzoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor:
      theme.palette.mode === "light" ? "#fdf3f3" : "rgba(161,58,58,0.12)",
  },
  mediaDropzoneHint: {
    fontSize: 11,
    opacity: 0.75,
  },
}));

const handleStyleOf = (theme) => ({
  width: 8,
  height: 8,
  background: "#a13a3a",
  border: `2px solid ${theme.palette.background.paper}`,
});

const nodeCardStyleOf = (theme) => ({
  width: NODE_WIDTH,
  background: theme.palette.background.paper,
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 1px 3px rgba(16,24,40,0.08), 0 1px 2px rgba(16,24,40,0.06)",
  padding: "10px 12px",
  fontFamily: '"Inter", "Roboto", sans-serif',
  cursor: "pointer",
  transition: "box-shadow 0.15s ease, transform 0.15s ease",
});

const MenuOptionNode = ({ data, selected }) => {
  const theme = useTheme();
  const nodeCardStyle = nodeCardStyleOf(theme);
  const handleStyle = handleStyleOf(theme);
  return (
    <div
      style={{
        ...nodeCardStyle,
        border: selected ? "1.5px solid #a13a3a" : nodeCardStyle.border,
        boxShadow: selected
          ? "0 4px 12px rgba(161,58,58,0.25)"
          : nodeCardStyle.boxShadow,
      }}
    >
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 8,
            background: "linear-gradient(135deg, #c05f5f, #a13a3a)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 13,
            flexShrink: 0,
          }}
        >
          {data.option || "?"}
        </div>
        <span
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {data.title || "(sin título)"}
        </span>
        {data.hasMedia && (
          <AttachFileIcon style={{ fontSize: 15, color: theme.palette.text.disabled }} />
        )}
      </div>
      <div
        style={{
          fontSize: 11.5,
          color: theme.palette.text.secondary,
          marginTop: 6,
          lineHeight: 1.4,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          minHeight: 16,
        }}
      >
        {data.message || i18n.t("queueFlowBuilder.noMessage")}
      </div>
      <Handle type="source" position={Position.Bottom} style={handleStyle} />
    </div>
  );
};

const RootNode = () => {
  const theme = useTheme();
  return (
  <div
    style={{
      ...nodeCardStyleOf(theme),
      background: "linear-gradient(135deg, #c05f5f, #7a2c2c)",
      border: "none",
      color: "#fff",
      textAlign: "center",
      cursor: "default",
      boxShadow: "0 4px 14px rgba(161,58,58,0.35)",
    }}
  >
    <div style={{ fontWeight: 700, fontSize: 13.5, letterSpacing: 0.2 }}>
      {i18n.t("queueFlowBuilder.rootTitle")}
    </div>
    <div style={{ fontSize: 11, opacity: 0.9, marginTop: 3 }}>
      {i18n.t("queueFlowBuilder.rootSubtitle")}
    </div>
    <Handle type="source" position={Position.Bottom} style={handleStyleOf(theme)} />
  </div>
  );
};

const nodeTypes = { menuOption: MenuOptionNode, rootNode: RootNode };

const layoutTree = (options) => {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", nodesep: 40, ranksep: 70 });
  g.setDefaultEdgeLabel(() => ({}));

  g.setNode(ROOT_ID, { width: NODE_WIDTH, height: NODE_HEIGHT });
  options.forEach((o) => {
    g.setNode(String(o.id), { width: NODE_WIDTH, height: NODE_HEIGHT });
  });
  options.forEach((o) => {
    g.setEdge(o.parentId ? String(o.parentId) : ROOT_ID, String(o.id));
  });

  dagre.layout(g);

  const positions = {};
  g.nodes().forEach((id) => {
    const { x, y } = g.node(id);
    positions[id] = { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 };
  });
  return positions;
};

const QueueFlowBuilder = ({ queueId }) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const [options, setOptions] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState({ title: "", option: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const mediaInputRef = useRef(null);

  const selectedOption = useMemo(
    () => options.find((o) => String(o.id) === String(selectedId)),
    [options, selectedId]
  );

  const selectedHasChildren = useMemo(
    () =>
      selectedOption
        ? options.some((o) => String(o.parentId) === String(selectedOption.id))
        : false,
    [options, selectedOption]
  );

  const fetchTree = useCallback(async () => {
    if (!queueId) return;
    try {
      const { data } = await api.get("/queue-options", {
        params: { queueId },
      });
      setOptions(data || []);
    } catch (err) {
      toastError(err);
    }
  }, [queueId]);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  useEffect(() => {
    const needsLayout = options.some(
      (o) => o.positionX == null || o.positionY == null
    );
    const layout = needsLayout ? layoutTree(options) : null;

    const builtNodes = [
      {
        id: ROOT_ID,
        type: "rootNode",
        position: layout ? layout[ROOT_ID] : { x: 40, y: 20 },
        data: {},
        draggable: false,
        selectable: false,
      },
      ...options.map((o) => ({
        id: String(o.id),
        type: "menuOption",
        position:
          o.positionX != null && o.positionY != null
            ? { x: o.positionX, y: o.positionY }
            : layout[String(o.id)],
        data: {
          option: o.option,
          title: o.title,
          message: o.message,
          hasMedia: Boolean(o.mediaPath),
        },
      })),
    ];

    const builtEdges = options.map((o) => ({
      id: `e-${o.id}`,
      source: o.parentId ? String(o.parentId) : ROOT_ID,
      target: String(o.id),
      type: "smoothstep",
      style: { stroke: theme.palette.mode === "light" ? "#c3cad2" : theme.palette.divider, strokeWidth: 1.5 },
    }));

    setNodes(builtNodes);
    setEdges(builtEdges);
  }, [options, theme.palette.mode]);

  useEffect(() => {
    if (selectedOption) {
      setForm({
        title: selectedOption.title || "",
        option: selectedOption.option || "",
        message: selectedOption.message || "",
      });
    }
  }, [selectedOption]);

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  const onNodeClick = useCallback((_, node) => {
    if (node.id !== ROOT_ID) setSelectedId(node.id);
  }, []);

  const onNodeDragStop = useCallback(async (_, node) => {
    if (node.id === ROOT_ID) return;
    try {
      await api.put(`/queue-options/${node.id}`, {
        positionX: node.position.x,
        positionY: node.position.y,
      });
      setOptions((prev) =>
        prev.map((o) =>
          String(o.id) === node.id
            ? { ...o, positionX: node.position.x, positionY: node.position.y }
            : o
        )
      );
    } catch (err) {
      toastError(err);
    }
  }, []);

  const handleAddOption = async (parentId = null) => {
    const siblings = options.filter(
      (o) => String(o.parentId || "") === String(parentId || "")
    );
    try {
      const { data } = await api.post("/queue-options", {
        queueId,
        parentId,
        title: i18n.t("queueFlowBuilder.defaultOptionTitle"),
        option: String(siblings.length + 1),
        message: "",
      });
      await fetchTree();
      if (data?.id) setSelectedId(String(data.id));
    } catch (err) {
      toastError(err);
    }
  };

  const handleSave = async () => {
    if (!selectedOption) return;
    setSaving(true);
    try {
      await api.put(`/queue-options/${selectedOption.id}`, {
        title: form.title,
        option: form.option,
        message: form.message,
      });
      toast.success(i18n.t("queueFlowBuilder.toasts.optionSaved"));
      await fetchTree();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!selectedOption) return;
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedOption) return;
    try {
      await api.delete(`/queue-options/${selectedOption.id}`);
      setSelectedId(null);
      await fetchTree();
    } catch (err) {
      toastError(err);
    }
  };

  const uploadFile = async (file) => {
    if (!file || !selectedOption) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(
        `/queue-options/${selectedOption.id}/media-upload`,
        formData
      );
      toast.success(i18n.t("queueFlowBuilder.toasts.fileAttached"));
      await fetchTree();
    } catch (err) {
      toastError(err);
    }
  };

  const handleMediaUpload = async (e) => {
    await uploadFile(e.target.files?.[0]);
    e.target.value = "";
  };

  const handleMediaDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleMediaDragLeave = () => {
    setDragActive(false);
  };

  const handleMediaDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    await uploadFile(e.dataTransfer.files?.[0]);
  };

  const handleMediaDelete = async () => {
    if (!selectedOption) return;
    try {
      await api.delete(`/queue-options/${selectedOption.id}/media-upload`);
      await fetchTree();
    } catch (err) {
      toastError(err);
    }
  };

  const handleAutoLayout = async () => {
    const layout = layoutTree(options);
    try {
      await Promise.all(
        options.map((o) =>
          api.put(`/queue-options/${o.id}`, {
            positionX: layout[String(o.id)].x,
            positionY: layout[String(o.id)].y,
          })
        )
      );
      await fetchTree();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.wrapper}>
      <ConfirmationModal
        title={i18n.t("queueFlowBuilder.deleteModal.title")}
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={confirmDelete}
      >
        {selectedHasChildren
          ? i18n.t("queueFlowBuilder.deleteModal.messageWithChildren")
          : i18n.t("queueFlowBuilder.deleteModal.message")}
      </ConfirmationModal>
      <div className={classes.toolbar}>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleAddOption(null)}
        >
          {i18n.t("queueFlowBuilder.buttons.addOption")}
        </Button>
        <Tooltip title={i18n.t("queueFlowBuilder.buttons.organizeTooltip")}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AccountTreeIcon />}
            onClick={handleAutoLayout}
            disabled={options.length === 0}
          >
            {i18n.t("queueFlowBuilder.buttons.organize")}
          </Button>
        </Tooltip>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onNodeClick={onNodeClick}
        onNodeDragStop={onNodeDragStop}
        fitView
        fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
        proOptions={{ hideAttribution: true }}
        nodesConnectable={false}
        deleteKeyCode={null}
        colorMode={theme.palette.mode}
      >
        <Background gap={18} size={1} />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable style={{ width: 120, height: 80 }} />
      </ReactFlow>

      {selectedOption && (
        <div className={classes.editorPanel}>
          <div className={classes.editorHeader}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {i18n.t("queueFlowBuilder.editTitle")}
            </Typography>
            <IconButton size="small" onClick={() => setSelectedId(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div className={classes.fieldRow}>
            <Tooltip title={i18n.t("queueFlowBuilder.form.numberTooltip")}>
              <TextField
                label={i18n.t("queueFlowBuilder.form.number")}
                size="small"
                className={classes.numberField}
                value={form.option}
                onChange={(e) => setForm((f) => ({ ...f, option: e.target.value }))}
              />
            </Tooltip>
            <TextField
              label={i18n.t("queueFlowBuilder.form.title")}
              size="small"
              fullWidth
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </div>
          <TextField
            label={i18n.t("queueFlowBuilder.form.message")}
            size="small"
            fullWidth
            multiline
            rows={5}
            value={form.message}
            onChange={(e) =>
              setForm((f) => ({ ...f, message: e.target.value }))
            }
          />

          <input
            type="file"
            hidden
            ref={mediaInputRef}
            onChange={handleMediaUpload}
          />
          {selectedOption.mediaPath ? (
            <div className={classes.mediaRow}>
              <AttachFileIcon fontSize="small" />
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                {selectedOption.mediaName || i18n.t("queueFlowBuilder.media.attachedFile")}
              </span>
              <IconButton size="small" color="error" onClick={handleMediaDelete}>
                <DeleteOutlinedIcon fontSize="small" />
              </IconButton>
            </div>
          ) : (
            <div
              className={`${classes.mediaDropzone} ${
                dragActive ? classes.mediaDropzoneActive : ""
              }`}
              onClick={() => mediaInputRef.current?.click()}
              onDragOver={handleMediaDragOver}
              onDragLeave={handleMediaDragLeave}
              onDrop={handleMediaDrop}
            >
              <AttachFileIcon fontSize="small" />
              <span>{i18n.t("queueFlowBuilder.media.attach")}</span>
              <span className={classes.mediaDropzoneHint}>
                {i18n.t("queueFlowBuilder.media.dropHint")}
              </span>
            </div>
          )}

          <div className={classes.editorActions}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {i18n.t("queueFlowBuilder.buttons.save")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleAddOption(selectedOption.id)}
            >
              {i18n.t("queueFlowBuilder.buttons.addSubOption")}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlinedIcon />}
              onClick={handleDelete}
            >
              {i18n.t("queueFlowBuilder.buttons.deleteOption")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueueFlowBuilder;
